import { gql } from '@apollo/client';
import { apolloClient } from '../config/apollo-client';

// GraphQL query for fetching game sessions
export const GET_GAME_SESSIONS_QUERY = gql`
  query GetGameSessions($input: GetGameSessionsInput!) {
    getGameSessions(input: $input) {
      sessions {
        id
        userId
        username
        gameId
        providerId
        sessionToken
        isDemo
        status
        balanceStart
        balanceEnd
        ipAddress
        createdAt
        endedAt
        gameName
      }
      total
    }
  }
`;

// Types for the response
export interface GameSession {
  id: number;
  userId?: number;
  username?: string;
  gameId: string;
  providerId: number;
  sessionToken: string;
  isDemo: boolean;
  status: string;
  balanceStart: number;
  balanceEnd?: number;
  ipAddress?: string;
  createdAt: string;
  endedAt?: string;
  gameName?: string;
}

export interface GameSessionsResponse {
  sessions: GameSession[];
  total: number;
}

export interface GetGameSessionsResponse {
  getGameSessions: GameSessionsResponse;
}

// Input types for filtering
export interface GetGameSessionsInput {
  userId?: number;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}

// GetGameSessions service class
export class GetGameSessionsService {
  private static instance: GetGameSessionsService;

  private constructor() {}

  public static getInstance(): GetGameSessionsService {
    if (!GetGameSessionsService.instance) {
      GetGameSessionsService.instance = new GetGameSessionsService();
    }
    return GetGameSessionsService.instance;
  }

  // Fetch game sessions with filters
  async getGameSessions(
    input: GetGameSessionsInput = {},
  ): Promise<GameSessionsResponse> {
    try {
      const { data } = await apolloClient.query<GetGameSessionsResponse>({
        query: GET_GAME_SESSIONS_QUERY,
        variables: {
          input,
        },
        fetchPolicy: 'network-only',
      });
      return data.getGameSessions;
    } catch (error: any) {
      console.error('Error fetching game sessions:', error);
      // Log more detailed error information
      if (error.graphQLErrors) {
        error.graphQLErrors.forEach((err: any) => {
          console.error('GraphQL Error:', err.message);
          console.error('Locations:', err.locations);
          console.error('Path:', err.path);
        });
      }
      if (error.networkError) {
        console.error('Network Error:', error.networkError);
      }
      throw error;
    }
  }

  // Fetch game sessions for a specific user
  async getGameSessionsByUserId(
    userId: number,
    limit: number = 50,
    offset: number = 0,
  ): Promise<GameSessionsResponse> {
    return this.getGameSessions({
      userId,
      limit,
      offset,
    });
  }

  // Fetch game sessions with date range
  async getGameSessionsByDateRange(
    startDate: string,
    endDate: string,
    limit: number = 50,
    offset: number = 0,
  ): Promise<GameSessionsResponse> {
    return this.getGameSessions({
      startDate,
      endDate,
      limit,
      offset,
    });
  }

  // Fetch all game sessions for current user
  async getAllGameSessions(
    limit: number = 50,
    offset: number = 0,
  ): Promise<GameSessionsResponse> {
    return this.getGameSessions({
      limit,
      offset,
    });
  }
}

// Export singleton instance
export const getGameSessionsService = GetGameSessionsService.getInstance();

// Export convenience function for backward compatibility
export async function getGameSessions(
  input: GetGameSessionsInput = {},
): Promise<GameSessionsResponse> {
  return getGameSessionsService.getGameSessions(input);
}
